/**
 * Commercial Readiness: System Health Dashboard
 * 
 * Operational control center for SaaS monitoring
 * Displays: API Health, MongoDB, Providers, Queues, Security, Backup, Deployment, Uptime, Errors
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Heart,
  Server,
  Database,
  Shield,
  HardDrive,
  Rocket,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Activity,
  Zap,
  TrendingUp,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// /system/health reports null for anything the app can't measure - show
// that as "Unknown", never as a made-up 0 / default.
const orUnknown = (value, suffix = '') => (value === null || value === undefined || value === '' ? 'Unknown' : `${value}${suffix}`);
const formatUptime = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Unknown';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const OFFSITE_STATES = {
  ok: { label: 'Off-site: OK', color: 'text-green-400' },
  failing: { label: 'Last upload failed', color: 'text-yellow-400' },
  stale: { label: 'Stale', color: 'text-red-400' },
  never_succeeded: { label: 'No copy yet', color: 'text-red-400' },
  misconfigured: { label: 'Misconfigured', color: 'text-red-400' },
  not_configured: { label: 'Not configured', color: 'text-yellow-400' },
};
const labelize = (value) => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SystemHealthDashboard = ({ user }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  // /monitoring/alerts and /monitoring/queue-status are separate, richer
  // endpoints server.py already computes from live data (severity + a
  // recommended action per alert; a full breakdown per queue status
  // including background_jobs, which /system/health's own inline "queues"
  // summary doesn't have at all) - fully built, never called from any UI.
  const [alerts, setAlerts] = useState([]);
  const [queueDetail, setQueueDetail] = useState(null);
  // GET/POST /admin/email/queue, /admin/email/process-queue and
  // /admin/email/retry/{id} (server.py) are fully built - real queue
  // stats, per-email detail, manual reprocessing, per-email retry - but
  // had no caller anywhere in the frontend. SystemHealthDashboard's own
  // "queues" summary already surfaces an email_queue *count*, with no way
  // to see which emails failed, why, or do anything about it.
  const [emailQueue, setEmailQueue] = useState(null);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [retryingEmailId, setRetryingEmailId] = useState(null);
  // GET /admin/environment/validate (validate_env.py) is fully built - flags
  // a default/weak JWT_SECRET, wildcard CORS, default demo password, and
  // live DB/Stripe connectivity - but had no caller anywhere in the
  // frontend, so a production-readiness misconfiguration was invisible.
  const [envValidation, setEnvValidation] = useState(null);
  // GET /monitoring/errors returns full error_logs documents (stack_trace,
  // endpoint, user_email, request_data) with severity filtering and no
  // 5-row cap - but only its degraded substitute was ever wired up: the
  // "Recent Errors" card above reads health.recent_errors, which
  // GET /system/health caps at 5 rows and only 4 fields, dropping the
  // stack trace and which user/route hit it entirely.
  const [errorLogs, setErrorLogs] = useState(null);
  // GET /admin/backup/offsite-status (Master Admin): last VERIFIED off-site
  // upload, its size/age, and honest "not configured" / "stale" states. Never
  // includes credentials. null = not loaded (or request failed) -> "Unknown".
  const [offsite, setOffsite] = useState(null);
  const [expandedErrorId, setExpandedErrorId] = useState(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchHealthData = useCallback(async () => {
    setChecking(true);
    try {
      const [healthRes, alertsRes, queueRes, emailQueueRes, envValidationRes, errorLogsRes, offsiteRes] = await Promise.all([
        fetch(`${API}/system/health`, { headers: authHeaders() }),
        fetch(`${API}/monitoring/alerts`, { headers: authHeaders() }),
        fetch(`${API}/monitoring/queue-status`, { headers: authHeaders() }),
        fetch(`${API}/admin/email/queue`, { headers: authHeaders() }),
        fetch(`${API}/admin/environment/validate`, { headers: authHeaders() }),
        fetch(`${API}/monitoring/errors?limit=50`, { headers: authHeaders() }),
        fetch(`${API}/admin/backup/offsite-status`, { headers: authHeaders() }),
      ]);
      if (healthRes.ok) {
        setHealth(await healthRes.json());
      } else {
        toast.error('Failed to fetch system health');
      }
      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts || []);
      }
      if (queueRes.ok) {
        setQueueDetail(await queueRes.json());
      }
      if (emailQueueRes.ok) {
        setEmailQueue(await emailQueueRes.json());
      }
      if (envValidationRes.ok) {
        setEnvValidation(await envValidationRes.json());
      }
      if (errorLogsRes.ok) {
        const errorLogsData = await errorLogsRes.json();
        setErrorLogs(errorLogsData.errors || []);
      }
      setOffsite(offsiteRes.ok ? await offsiteRes.json() : null);
    } catch (error) {
      console.error('Error fetching health:', error);
      toast.error('System health check failed');
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, [authHeaders]);

  const processEmailQueue = async () => {
    setProcessingQueue(true);
    try {
      const response = await fetch(`${API}/admin/email/process-queue`, { method: 'POST', headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Email queue processed');
        await fetchHealthData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process email queue');
      }
    } catch (error) {
      toast.error('Error processing email queue');
    } finally {
      setProcessingQueue(false);
    }
  };

  const retryEmail = async (emailId) => {
    setRetryingEmailId(emailId);
    try {
      const response = await fetch(`${API}/admin/email/retry/${emailId}`, { method: 'POST', headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        const status = data.result?.status;
        if (status === 'sent') {
          toast.success('Email sent');
        } else if (status === 'provider_not_configured') {
          toast.error('SendGrid is not configured - email remains queued');
        } else {
          toast.error(data.result?.error || 'Retry failed - see last_error for details');
        }
        await fetchHealthData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to retry email');
      }
    } catch (error) {
      toast.error('Error retrying email');
    } finally {
      setRetryingEmailId(null);
    }
  };

  useEffect(() => {
    fetchHealthData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, [fetchHealthData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return 'text-green-400';
      case 'degraded':
      case 'warning':
        return 'text-yellow-400';
      case 'unhealthy':
      case 'critical':
      case 'down':
        return 'text-red-400';
      default:
        return 'text-white/60';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return <CheckCircle2 className="h-5 w-5 text-green-400" />;
      case 'degraded':
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
      case 'unhealthy':
      case 'critical':
      case 'down':
        return <XCircle className="h-5 w-5 text-red-400" />;
      default:
        return <Activity className="h-5 w-5 text-white/60" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
      case 'operational':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30">Healthy</Badge>;
      case 'degraded':
      case 'warning':
        return <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">Degraded</Badge>;
      case 'unhealthy':
      case 'critical':
      case 'down':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/30">Unhealthy</Badge>;
      default:
        return <Badge className="bg-white/10 text-white/60 border-white/20">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="page-stack" data-testid="system-health-dashboard">
        <section className="hero-panel ceo-hero glass-panel noise-overlay">
          <div>
            <p className="eyebrow">Master Admin Only — Operational Control Center</p>
            <h2>System Health Dashboard</h2>
            <p className="text-white/70">Loading system health data...</p>
          </div>
        </section>
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="glass-panel border-white/10">
              <CardContent className="pt-6">
                <Skeleton className="h-32 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </section>
      </div>
    );
  }

  const services = health?.services || {};
  const overall_status = health?.status || 'unknown';

  return (
    <div className="page-stack" data-testid="system-health-dashboard">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Master Admin Only — Operational Control Center</p>
          <h2>System Health Dashboard</h2>
          <div className="hero-stats">
            <span className={getStatusColor(overall_status)}>
              Overall: {overall_status.toUpperCase()}
            </span>
            <span>{Object.keys(services).length} Services Monitored</span>
            <span>Last Check: {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}</span>
          </div>
        </div>
        <Button 
          className="primary-btn" 
          onClick={fetchHealthData}
          disabled={checking}
          data-testid="refresh-health-button"
        >
          {checking ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Heart size={16} />
              Refresh Health Check
            </>
          )}
        </Button>
      </section>

      {/* Critical Alerts */}
      {health?.critical_alerts && health.critical_alerts.length > 0 && (
        <Alert className="bg-red-500/20 border-red-500/30">
          <AlertTriangle className="h-4 w-4 text-red-300" />
          <AlertDescription>
            <strong className="text-red-300">Critical Alerts:</strong>
            <ul className="mt-2 space-y-1">
              {health.critical_alerts.map((alert, index) => (
                <li key={index} className="text-red-300/90 text-sm">• {alert}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Service Status Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* API Health */}
        <Card className="glass-panel border-white/10" data-testid="health-api-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">API Health</CardTitle>
              </div>
              {getStatusIcon(services.api?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.api?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Response Time:</span>
                <span className="text-white/90">{services.api?.response_time_ms || 0}ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Endpoints:</span>
                <span className="text-white/90">{services.api?.total_endpoints || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Version:</span>
                <span className="text-white/90">{services.api?.version || 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MongoDB Health */}
        <Card className="glass-panel border-white/10" data-testid="health-mongodb-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">MongoDB</CardTitle>
              </div>
              {getStatusIcon(services.mongodb?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.mongodb?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Collections:</span>
                <span className="text-white/90">{services.mongodb?.collections_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Total Records:</span>
                <span className="text-white/90">{services.mongodb?.total_records || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Connection:</span>
                <span className="text-white/90">{services.mongodb?.connected ? 'Active' : 'Disconnected'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Provider Health */}
        <Card className="glass-panel border-white/10" data-testid="health-providers-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Providers</CardTitle>
              </div>
              {getStatusIcon(services.providers?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.providers?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Total Providers:</span>
                <span className="text-white/90">{services.providers?.total || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Enabled:</span>
                <span className="text-white/90">{services.providers?.enabled || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">With Credentials:</span>
                <span className="text-white/90">{services.providers?.configured || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Enabled + Credentials:</span>
                <span className="text-white/90">{services.providers?.healthy || 0}</span>
              </div>
              {services.providers?.enabled_without_credentials?.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/60">Enabled, no key:</span>
                  <span className="text-yellow-300">{services.providers.enabled_without_credentials.join(', ')}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Queue Status */}
        <Card className="glass-panel border-white/10" data-testid="health-queues-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Queues</CardTitle>
              </div>
              {getStatusIcon(services.queues?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.queues?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Email Queue:</span>
                <span className="text-white/90">{services.queues?.email_queue || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Enrichment Queue:</span>
                <span className="text-white/90">{services.queues?.enrichment_queue || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Failed Jobs:</span>
                <span className="text-white/90">{services.queues?.failed_jobs || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Status */}
        <Card className="glass-panel border-white/10" data-testid="health-security-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Security</CardTitle>
              </div>
              {getStatusIcon(services.security?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.security?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">JWT Secret:</span>
                <span className="text-white/90">{services.security?.jwt_configured ? 'Configured' : 'Missing'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Encryption Key:</span>
                <span className="text-white/90">{services.security?.encryption_configured ? 'Configured' : 'Missing'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Rate Limiting:</span>
                <span className="text-white/90">{services.security?.rate_limiting_enabled ? 'Active' : 'Disabled'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backup Status */}
        <Card className="glass-panel border-white/10" data-testid="health-backup-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Backups</CardTitle>
              </div>
              {getStatusIcon(services.backups?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.backups?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Last Backup:</span>
                <span className="text-white/90">{services.backups?.last_backup_at ? new Date(services.backups.last_backup_at).toLocaleDateString() : 'Never'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Backup Count:</span>
                <span className="text-white/90">{services.backups?.backup_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">MongoDB Atlas:</span>
                <span className="text-white/90">{services.backups?.atlas_enabled === true ? 'Enabled' : services.backups?.atlas_enabled === false ? 'Manual' : 'Unknown'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Off-site backup (encrypted S3-compatible copy) - honest about not being configured */}
        <Card className="glass-panel border-white/10" data-testid="health-offsite-backup-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Off-site Backup</CardTitle>
              </div>
              {offsite?.state === 'ok' ? getStatusIcon('healthy') : getStatusIcon(offsite ? 'warning' : 'unknown')}
            </div>
          </CardHeader>
          <CardContent>
            <span
              className={`text-sm font-medium ${(OFFSITE_STATES[offsite?.state] || {}).color || 'text-white/60'}`}
              data-testid="offsite-backup-state"
            >
              {offsite ? (OFFSITE_STATES[offsite.state]?.label || labelize(offsite.state)) : 'Unknown'}
            </span>
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Last verified copy:</span>
                <span className="text-white/90" data-testid="offsite-backup-last">
                  {offsite?.last_success_at ? new Date(offsite.last_success_at).toLocaleString() : offsite ? 'Never' : 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Size (encrypted):</span>
                <span className="text-white/90">{offsite?.last_success_at ? formatBytes(offsite.last_success_size_bytes) : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Age:</span>
                <span className="text-white/90">{offsite?.age_hours !== null && offsite?.age_hours !== undefined ? `${offsite.age_hours}h` : '-'}</span>
              </div>
            </div>
            {offsite?.warning && (
              <p className="mt-3 text-xs text-yellow-300" data-testid="offsite-backup-warning">{offsite.warning}</p>
            )}
            {offsite?.last_error && offsite.state !== 'ok' && (
              <p className="mt-1 text-xs text-white/50 break-words">Last error: {offsite.last_error}</p>
            )}
            {offsite && offsite.missing_settings?.length > 0 && offsite.state === 'misconfigured' && (
              <p className="mt-1 text-xs text-white/50 break-words">Missing: {offsite.missing_settings.join(', ')}</p>
            )}
          </CardContent>
        </Card>

        {/* Deployment Status */}
        <Card className="glass-panel border-white/10" data-testid="health-deployment-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Deployment</CardTitle>
              </div>
              {getStatusIcon(services.deployment?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.deployment?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Environment:</span>
                <span className="text-white/90">{orUnknown(services.deployment?.environment)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Build:</span>
                <span className="text-white/90">{orUnknown(services.deployment?.build_version)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Last Deploy:</span>
                <span className="text-white/90">{services.deployment?.last_deploy_at ? new Date(services.deployment.last_deploy_at).toLocaleDateString() : 'Unknown'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scheduled Jobs (db.scheduled_jobs, written by every job run) */}
        <Card className="glass-panel border-white/10" data-testid="health-scheduler-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Scheduled Jobs</CardTitle>
              </div>
              {getStatusIcon(services.scheduler?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.scheduler?.status)}
            <div className="mt-4 space-y-2 text-xs">
              {(services.scheduler?.jobs || []).map((job) => (
                <div key={job.job} className="flex justify-between gap-2">
                  <span className="text-white/60">{labelize(job.job)}:</span>
                  <span className={job.last_status === 'error' ? 'text-red-300' : job.overdue ? 'text-yellow-300' : 'text-white/90'}>
                    {job.last_status === 'never_run' ? 'Never run' : labelize(job.last_status)}
                    {job.last_run_at ? ` · ${new Date(job.last_run_at).toLocaleString()}` : ''}
                    {job.overdue ? ' · overdue' : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Uptime & Performance */}
        <Card className="glass-panel border-white/10" data-testid="health-uptime-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[var(--gold)]" />
                <CardTitle className="text-white/90 text-sm">Uptime & Performance</CardTitle>
              </div>
              {getStatusIcon(services.performance?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {getStatusBadge(services.performance?.status)}
            <div className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Process Uptime:</span>
                <span className="text-white/90">{formatUptime(services.performance?.process_uptime_seconds)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">DB Ping:</span>
                <span className="text-white/90">{orUnknown(services.mongodb?.ping_ms, 'ms')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Availability:</span>
                <span className="text-white/90">{orUnknown(services.performance?.uptime_percent, '%')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Error Rate:</span>
                <span className="text-white/90">{orUnknown(services.performance?.error_rate_percent, '%')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Detailed Service Information */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[var(--gold)]" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {health?.performance_metrics && Object.entries(health.performance_metrics).map(([key, value]) => (
                <div key={key} className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-sm text-white/70">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="text-sm font-semibold text-white/90">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[var(--gold)]" />
              Recent Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {health?.recent_errors && health.recent_errors.length > 0 ? (
              <div className="space-y-2">
                {health.recent_errors.slice(0, 5).map((error, index) => (
                  <div key={index} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-300 font-mono">{error.message || error}</p>
                    {error.timestamp && (
                      <p className="text-xs text-red-300/60 mt-1">{new Date(error.timestamp).toLocaleString()}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-2" />
                <p className="text-white/60 text-sm">No recent errors</p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Monitoring Alerts + Queue/Background Job Detail */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass-panel border-white/10" data-testid="health-monitoring-alerts-card">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[var(--gold)]" />
              Monitoring Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-2">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${alert.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}
                  >
                    <p className={`text-xs font-semibold ${alert.severity === 'critical' ? 'text-red-300' : 'text-yellow-300'}`}>
                      {(alert.type || alert.severity || 'alert').replace(/_/g, ' ').toUpperCase()}
                    </p>
                    <p className="text-sm text-white/80 mt-1">{alert.message}</p>
                    {alert.action && <p className="text-xs text-white/50 mt-1">Action: {alert.action}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-2" />
                <p className="text-white/60 text-sm">No active alerts</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10" data-testid="health-queue-detail-card">
          <CardHeader>
            <CardTitle className="text-white/90 flex items-center gap-2">
              <Activity className="h-5 w-5 text-[var(--gold)]" />
              Queue & Background Job Detail
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-xs">
              {[
                ['Email Queue', queueDetail?.email_queue],
                ['Enrichment Queue', queueDetail?.enrichment_queue],
                ['Background Jobs', queueDetail?.background_jobs],
              ].map(([label, detail]) => (
                <div key={label}>
                  <p className="text-white/70 font-semibold mb-1">{label}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(detail || {}).filter(([key]) => key !== 'total').map(([key, value]) => (
                      <div key={key} className="flex justify-between border-b border-white/10 pb-1">
                        <span className="text-white/50">{labelize(key)}:</span>
                        <span className="text-white/90">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!queueDetail && <p className="text-white/60 text-center py-4">Loading queue detail...</p>}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Email Queue */}
      <Card className="glass-panel border-white/10" data-testid="health-email-queue-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white/90 flex items-center gap-2">
              <Mail className="h-5 w-5 text-[var(--gold)]" />
              Email Queue
            </CardTitle>
            <Button className="ghost-btn small" onClick={processEmailQueue} disabled={processingQueue} data-testid="process-email-queue-button">
              {processingQueue ? 'Processing...' : 'Process Queue Now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <p className="text-xs text-white/60 uppercase">Queued</p>
              <p className="text-xl font-semibold text-white/90">{emailQueue?.statistics?.queued ?? 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-white/60 uppercase">Sent</p>
              <p className="text-xl font-semibold text-green-400">{emailQueue?.statistics?.sent ?? 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-white/60 uppercase">Failed</p>
              <p className="text-xl font-semibold text-red-400">{emailQueue?.statistics?.failed ?? 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-white/60 uppercase">Provider Not Configured</p>
              <p className="text-xl font-semibold text-yellow-400">{emailQueue?.statistics?.provider_not_configured ?? 0}</p>
            </div>
          </div>
          {emailQueue && !emailQueue.sendgrid_configured && (
            <Alert className="bg-yellow-500/20 border-yellow-500/30 mb-4">
              <AlertTriangle className="h-4 w-4 text-yellow-300" />
              <AlertDescription className="text-yellow-300/90 text-sm">
                SendGrid is not configured - queued emails will accumulate until an API key is set.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            {(emailQueue?.recent_emails || []).slice(0, 15).map((email) => (
              <div key={email.id} className="flex items-center justify-between p-3 border border-white/10 rounded-lg" data-testid={`email-queue-row-${email.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white/90 truncate">{email.subject || '(no subject)'}</p>
                  <p className="text-xs text-white/60 truncate">{email.recipient} • {email.created_at ? new Date(email.created_at).toLocaleString() : ''}</p>
                  {email.last_error && <p className="text-xs text-red-300/80 mt-1 truncate">{email.last_error}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <Badge className={
                    email.status === 'sent' ? 'bg-green-500/20 text-green-300 border-green-500/30'
                    : email.status === 'failed' ? 'bg-red-500/20 text-red-300 border-red-500/30'
                    : email.status === 'provider_not_configured' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                    : 'bg-white/10 text-white/60 border-white/20'
                  }>
                    {labelize(email.status)}
                  </Badge>
                  {(email.status === 'failed' || email.status === 'provider_not_configured') && (
                    <Button
                      className="ghost-btn small"
                      onClick={() => retryEmail(email.id)}
                      disabled={retryingEmailId === email.id}
                      data-testid={`retry-email-${email.id}-button`}
                    >
                      {retryingEmailId === email.id ? 'Retrying...' : 'Retry'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {emailQueue && !emailQueue.recent_emails?.length && (
              <p className="text-white/60 text-center py-4">No emails in the queue yet.</p>
            )}
            {!emailQueue && <p className="text-white/60 text-center py-4">Loading email queue...</p>}
          </div>
        </CardContent>
      </Card>

      {/* Error Log Detail */}
      <Card className="glass-panel border-white/10" data-testid="health-error-log-detail-card">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[var(--gold)]" />
            Error Log Detail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(errorLogs || []).map((err) => {
              const isExpanded = expandedErrorId === err.id;
              return (
                <div key={err.id} className="border border-white/10 rounded-lg overflow-hidden" data-testid={`error-log-row-${err.id}`}>
                  <button
                    type="button"
                    className="w-full text-left p-3 flex items-start justify-between gap-3 hover:bg-white/5"
                    onClick={() => setExpandedErrorId(isExpanded ? null : err.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-red-300 font-mono truncate">{err.message}</p>
                      <p className="text-xs text-white/50 mt-1">
                        {err.endpoint || 'unknown endpoint'} • {err.user_email || 'unknown user'}
                        {err.timestamp && ` • ${new Date(err.timestamp).toLocaleString()}`}
                      </p>
                    </div>
                    <Badge className={
                      err.severity === 'critical' ? 'bg-red-500/20 text-red-300 border-red-500/30'
                      : err.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      : 'bg-white/10 text-white/60 border-white/20'
                    }>
                      {labelize(err.severity || 'error')}
                    </Badge>
                  </button>
                  {isExpanded && (
                    <div className="p-3 border-t border-white/10 bg-black/20 space-y-2">
                      <p className="text-xs text-white/50">
                        Type: <span className="text-white/80">{labelize(err.error_type)}</span>
                      </p>
                      {err.stack_trace && (
                        <pre className="text-xs text-white/70 whitespace-pre-wrap break-words bg-black/30 p-2 rounded max-h-64 overflow-y-auto">
                          {err.stack_trace}
                        </pre>
                      )}
                      {err.metadata?.component_stack && (
                        <pre className="text-xs text-white/50 whitespace-pre-wrap break-words bg-black/30 p-2 rounded max-h-40 overflow-y-auto">
                          {err.metadata.component_stack}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {errorLogs && errorLogs.length === 0 && (
              <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-2" />
                <p className="text-white/60 text-sm">No errors logged</p>
              </div>
            )}
            {!errorLogs && <p className="text-white/60 text-center py-4">Loading error logs...</p>}
          </div>
        </CardContent>
      </Card>

      {/* Environment Validation */}
      <Card className="glass-panel border-white/10" data-testid="health-environment-validation-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white/90 flex items-center gap-2">
              <Shield className="h-5 w-5 text-[var(--gold)]" />
              Environment Validation
            </CardTitle>
            {envValidation && (
              envValidation.valid
                ? <Badge className="bg-green-500/20 text-green-300 border-green-500/30">Valid</Badge>
                : <Badge className="bg-red-500/20 text-red-300 border-red-500/30">Errors Found</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!envValidation && <p className="text-white/60 text-center py-4">Loading environment validation...</p>}
          {envValidation && (
            <div className="space-y-4">
              {envValidation.errors?.length > 0 && (
                <div className="space-y-1">
                  {envValidation.errors.map((msg, i) => (
                    <div key={i} className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300" data-testid={`env-error-${i}`}>
                      {msg}
                    </div>
                  ))}
                </div>
              )}
              {envValidation.warnings?.length > 0 && (
                <div className="space-y-1">
                  {envValidation.warnings.map((msg, i) => (
                    <div key={i} className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-300" data-testid={`env-warning-${i}`}>
                      {msg}
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {Object.entries(envValidation.checks || {})
                  .filter(([key]) => key !== 'database_connected' && key !== 'stripe')
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-white/10 pb-1">
                      <span className="text-white/50">{labelize(key)}:</span>
                      <span className="text-white/90">{String(value)}</span>
                    </div>
                  ))}
                <div className="flex justify-between border-b border-white/10 pb-1">
                  <span className="text-white/50">Database:</span>
                  <span className="text-white/90">{envValidation.checks?.database_connected ? 'Connected' : 'Disconnected'}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-1">
                  <span className="text-white/50">Stripe:</span>
                  <span className="text-white/90">{envValidation.checks?.stripe?.configured ? `Configured (${envValidation.checks.stripe.mode})` : 'Not Configured'}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Information */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <Server className="h-5 w-5 text-[var(--gold)]" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Environment</p>
              <p className="text-sm font-semibold text-white/90">{orUnknown(health?.environment)}</p>
            </div>
            <div>
              <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Build Version</p>
              <p className="text-sm font-semibold text-white/90">{orUnknown(health?.build_version)}</p>
            </div>
            <div>
              <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Database Name</p>
              <p className="text-sm font-semibold text-white/90">{health?.database_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Timestamp</p>
              <p className="text-sm font-semibold text-white/90">{health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthDashboard;
